import { init, isTMA, retrieveRawInitData } from '@telegram-apps/sdk-react'

/** Product dark surface — keep Telegram chrome in sync with DESIGN.md. */
const TG_BG = '#0c0e14'

type TelegramWebApp = {
  initData?: string
  ready?: () => void
  expand?: () => void
  setHeaderColor?: (color: string) => void
  setBackgroundColor?: (color: string) => void
  disableVerticalSwipes?: () => void
  HapticFeedback?: {
    impactOccurred?: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void
    selectionChanged?: () => void
  }
}

function getLegacyWebApp(): TelegramWebApp | undefined {
  try {
    return (
      window as unknown as { Telegram?: { WebApp?: TelegramWebApp } }
    ).Telegram?.WebApp
  } catch {
    return undefined
  }
}

/**
 * Align Mini App chrome with lobby surface: full height, header/bg color,
 * fewer accidental dismiss-swipes. Safe no-op outside Telegram.
 */
export function bootstrapTelegramUI(): void {
  try {
    const tg = getLegacyWebApp()
    if (!tg) return
    tg.ready?.()
    tg.expand?.()
    tg.setHeaderColor?.(TG_BG)
    tg.setBackgroundColor?.(TG_BG)
    tg.disableVerticalSwipes?.()
  } catch {
    /* ignore — browser / desktop preview */
  }
}

/** Light tap feedback for game entry (no-op outside Telegram). */
export function hapticLight(): void {
  try {
    getLegacyWebApp()?.HapticFeedback?.impactOccurred?.('light')
  } catch {
    /* ignore */
  }
}

/**
 * Bootstrap Telegram Mini App SDK and return raw initData string.
 * Outside Telegram, falls back to VITE_DEV_INIT_DATA for local development.
 */
export async function getInitData(): Promise<string> {
  const devFallback = (import.meta.env.VITE_DEV_INIT_DATA as string | undefined) ?? ''

  try {
    const inTma = await isTMA('complete')
    if (inTma) {
      init()
      bootstrapTelegramUI()
      const raw = retrieveRawInitData()
      if (raw) return raw
    }
  } catch {
    // Not a real TMA environment — try legacy bridge then env fallback.
  }

  // Legacy Telegram WebApp object (some clients still inject it).
  try {
    const legacy = getLegacyWebApp()
    bootstrapTelegramUI()
    if (legacy?.initData) return legacy.initData
  } catch {
    /* ignore */
  }

  return devFallback
}
